import { TAssetTypesDTO } from '@interfaces/map/dto/mapDTO';
import { AssetsFiltersDTO } from '@interfaces/map/dto/mapFiltersDTO';
import { IAssetTypeForFilter, IAttributesFilter } from '@interfaces/map/mapFilters';
import {
	ESiteAccessAvailability,
	TSiteAccessAvailability,
} from '@components/Map/MapControls/Controls/Filters/AssetsFilters/types';

import { EControlsType, TEControlsType } from '@enums/common';
import { EColorAssetsBy } from '@enums/map';

import { noSubTypeOptions } from '@components/routes/AdminPanel/AssetsList/constants';

export const getAssetTypeIdsWithSubtypes = (assetTypes: Array<IAssetTypeForFilter>) => {
	const result: Record<string, boolean> = {};

	assetTypes.forEach(type => {
		if (type?.id && type.subTypes && type.subTypes.length > 0 && typeof type.isItemsOpen === 'boolean') {
			result[type?.id] = type.isItemsOpen;
		}
	});

	return result;
};

interface IGetTooltipContentParams {
	name: TEControlsType;
	pinnedFilter?: TEControlsType | null;
	areBoroughsEnabled?: boolean;
	isAssetsMode?: boolean;
	isSectorsShown?: boolean;
}

export const getTooltipContent = ({
	name,
	pinnedFilter,
	areBoroughsEnabled,
	isAssetsMode,
	isSectorsShown,
}: IGetTooltipContentParams) => {
	const tooltips = {
		[EControlsType.createAssetMode]: 'Add a new asset',
		[EControlsType.filtersOokla]: 'Ookla data',
		[EControlsType.customPolygonControls]: pinnedFilter
			? 'Only one dialog can be pinned at a time'
			: areBoroughsEnabled || isSectorsShown
				? 'To make polygons available, turn off boroughs/sectors'
				: 'Create a polygon',
		[EControlsType.customPolygonList]: pinnedFilter
			? 'Only one dialog can be pinned at a time'
			: areBoroughsEnabled || isSectorsShown
				? 'To make polygons available, turn off boroughs/sectors'
				: 'Polygons',
		[EControlsType.filters]: pinnedFilter
			? 'Only one dialog can be pinned at a time'
			: isAssetsMode ? 'Asset filters' : 'Site filters',
		[EControlsType.owners]: pinnedFilter
			? 'Only one dialog can be pinned at a time'
			: isAssetsMode ? 'Asset owners' : 'Vendors',
		[EControlsType.mapSettings]: pinnedFilter ? 'Only one dialog can be pinned at a time' : 'Map view settings',
		[EControlsType.dashboardKpi]: pinnedFilter ? 'Only one dialog can be pinned at a time' : 'Dashboard KPI',
		[EControlsType.sectors]: pinnedFilter ? 'Only one dialog can be pinned at a time' : 'Sectors',
	};

	return tooltips[name];
};

export const extractIdsFromAssetTypes = (data: TAssetTypesDTO | undefined): string[] => {
	if (!data) return [];

	const ids = new Set<string>();

	for (const item of data) {
		if (typeof item === 'string') {
			ids.add(item);
		} else if (typeof item === 'object' && item !== null) {
			for (const key in item) {
				ids.add(key);

				const subTypes = item[key]?.subTypes;

				if (Array.isArray(subTypes)) {
					for (const subId of subTypes) {
						if (subId) {
							if (subId == noSubTypeOptions.value) {
								ids.add(`${key}-${subId}`);
							} else {
								ids.add(subId);
							}
						}
					}
				}
			}
		}
	}

	return Array.from(ids);
};

export const isYesNoAll = (value?: string): value is TSiteAccessAvailability => {
	return value === ESiteAccessAvailability.yes
		|| value === ESiteAccessAvailability.no
		|| value === ESiteAccessAvailability.all;
};

export const mapStringToBoolean = (value: string | undefined): boolean | undefined => {
	if (value === ESiteAccessAvailability.yes) {
		return true;
	}
	if (value === ESiteAccessAvailability.no) {
		return false;
	}
	return undefined;
};

export const getSelectedAssetTypeIds = (
	assetTypes: IAssetTypeForFilter[],
): string[] => {
	return assetTypes
		.reduce((acc: string[], assetType) => {
			if (!assetType.isSelected) return acc;

			acc.push(assetType.id);

			if (assetType.subTypes) {
				assetType.subTypes.forEach(subType => {
					if (subType.isSelected) {
						if (subType.subType === noSubTypeOptions.value) {
							acc.push(`${assetType.id}-${subType.subType}`);
						} else {
							acc.push(subType.id);
						}
					}
				});
			}

			return acc;
		}, []);
};

export const getSelectedAttributes = (attributes: IAttributesFilter[]): Record<string, string[]> => {
	return attributes.reduce<Record<string, string[]>>((acc, item) => {
		const { fieldName, items } = item;

		const selectedColorItems = items
			.filter(item => item.isSelected)
			.map(item => item.color);

		if (selectedColorItems.length) {
			acc[fieldName] = selectedColorItems;
		}

		return acc;
	}, {});
};

export const getAppliedFilterAttributes = (appliedFilters: AssetsFiltersDTO): Record<string, string[]> => {
	return Object.entries(appliedFilters).reduce<Record<string, string[]>>(
		(acc, [key, value]) => {
			if (
				key === EColorAssetsBy.siteAvailability ||
				key === EColorAssetsBy.backhaulAccessibility ||
				key === EColorAssetsBy.powerSupplyAvailability
			) {
				acc[key] = value;
			}
			return acc;
		},
		{},
	);
};
